import { FilterTypes, Options } from '@frontend/core'
import { useCallback, useMemo, useReducer } from 'react'

// Widest member of the FilterTypes union — the bound only has to accept every
// filter variant; call sites still resolve value/options against the concrete
// Filters they instantiate with.
type AnyFilter = FilterTypes<any, any>

export function useFilterReducer<Filters extends Record<string, AnyFilter>>(
  initialFilterState: Filters
) {
  const UPDATE_FILTER = 'UPDATE_FILTER'
  const UPDATE_OPTIONS = 'UPDATE_OPTIONS'
  const UPDATE_LABEL = 'UPDATE_LABEL'

  const filterReducer = (
    state: Filters,
    action:
      | {
          type: typeof UPDATE_FILTER
          payload: { name: keyof Filters; value: any }
        }
      | {
          type: typeof UPDATE_OPTIONS
          payload: {
            name: keyof Filters
            options: Options<any>
          }
        }
      | {
          type: typeof UPDATE_LABEL
          payload: {
            name: keyof Filters
            label: string
          }
        }
  ) => {
    switch (action.type) {
      case UPDATE_FILTER:
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            value: action.payload.value
          }
        }

      case UPDATE_OPTIONS:
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            options: action.payload.options
          }
        }

      case UPDATE_LABEL:
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            label: action.payload.label
          }
        }

      default:
        return state
    }
  }

  const [filters, dispatch] = useReducer(filterReducer, initialFilterState)

  const updateFilterValue = useCallback(
    <k extends keyof Filters>(name: k, value: Filters[k]['value']) => {
      dispatch({
        type: UPDATE_FILTER,
        payload: { name, value }
      })
    },
    []
  )

  const updateFilterOptions = useCallback(
    <k extends keyof Filters>(name: k, options: Filters[k]['options']) => {
      let newOptions = [...options]
      if (filters[name].type === 'singleWithAll') {
        newOptions = [{ name: 'All', value: filters[name].value }, ...options]
      }
      dispatch({
        type: UPDATE_OPTIONS,
        payload: { name, options: newOptions }
      })
    },
    []
  )

  const updateFilterLabel = useCallback(
    <k extends keyof Filters>(name: k, label: string) => {
      dispatch({
        type: UPDATE_LABEL,
        payload: { name, label }
      })
    },
    []
  )

  return useMemo(() => {
    return {
      filters,
      updateFilterValue,
      updateFilterOptions,
      updateFilterLabel
    }
  }, [filters, updateFilterValue, updateFilterOptions])
}
